import type { GroupingItem, GroupingGroup } from '@vedalib/editor/lib/elements'
import { useState, useEffect, useMemo } from 'react'

import { DeviceMode, PreviewMode } from 'services/Store/Project/enums'
import { shuffle } from 'utils/shuffle'

import { useElementCompleted } from '../../hooks/useElementCompleted'
import { useElementRootStyles } from '../../hooks/useElementRootStyles'
import type { IGroupingContextProps } from './GroupingContext'
import GroupingContext from './GroupingContext'
import * as s from './GroupingElement.module.scss'
import DefaultElement from './components/DefaultView/DefaultElement'
import DestinationCard from './components/DefaultView/DestinationCard'
import type { ValidationMap } from './components/DefaultView/PortalGroupingItem/PortalGroupingItem'
import PortalGroupingItem from './components/DefaultView/PortalGroupingItem/PortalGroupingItem'
import MobileElement from './components/MobileView/MobileElement'
import type { GroupingElementType } from './groupingTypes'
import { getCardsData, sortSourceByPreviewList } from './helper'

const PDF_STYLE = { position: 'relative' } as const

const GroupingElement: GroupingElementType = ({ element, styles, mode, block, font, feedback }) => {
  const [shuffledAnswers, setShuffledAnswers] = useState<GroupingItem[]>([])
  const [validationMap, setValidationMap] = useState<ValidationMap>({})
  const [testResult, setTestResult] = useState<{ [key: string]: boolean }>({})
  const [retry, setRetry] = useState(0)
  const [destination, setDestination] = useState<GroupingGroup[]>([])
  const rootStyles = useElementRootStyles(styles.root)

  const items = element.value.items as GroupingGroup[]
  const hasAnswerImage = element.value.hasAnswerImage
  const hasGroupImage = element.value.hasGroupImage
  const isMobile = mode.deviceMode === DeviceMode.mobile
  const isPdf = mode.previewMode === PreviewMode.pdf
  const pdfStyles = isPdf ? PDF_STYLE : undefined

  const contextValue = useMemo(
    (): IGroupingContextProps => ({
      destination,
      hasGroupImage,
      hasAnswerImage,
      styles,
      mode,
      retry,
      shuffledAnswers,
      testResult,
      validationMap,
      font,
      feedback,
      elementId: element.id,
      setDestination,
      setShuffledAnswers,
      setRetry,
      setTestResult,
      setValidationMap,
      pdfStyles,
    }),
    [
      destination,
      hasGroupImage,
      hasAnswerImage,
      styles,
      mode,
      retry,
      shuffledAnswers,
      testResult,
      validationMap,
      font,
      feedback,
      element.id,
      pdfStyles,
    ],
  )

  const setDataCards = (
    shuffledSource: GroupingItem[],
    dest: GroupingGroup[],
    validationMap: ValidationMap,
  ) => {
    setShuffledAnswers(shuffledSource)
    setValidationMap(validationMap)
    setDestination(dest)
  }

  // for shuffle only than, when added or removed any answer of any cards (allAnswersOfCarts.length)
  const allAnswersOfCarts = useMemo(() => {
    const { dest, source, validationMap } = getCardsData(items)
    const sortSource = sortSourceByPreviewList(source, shuffledAnswers)
    setDataCards(sortSource, dest, validationMap)
    return source
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  useEffect(() => {
    const { dest, source, validationMap } = getCardsData(items)
    const shuffledSource = shuffle(source)
    setDataCards(shuffledSource, dest, validationMap)
    // dependency allAnswersOfCarts.length need for shuffle
    // when change quantity(allAnswersOfCarts.length) answers of cards
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAnswersOfCarts.length, retry])

  const testResultNotEmpty = Object.keys(testResult).length > 0

  const completed = testResultNotEmpty && !shuffledAnswers.length

  useElementCompleted({ block, elementId: element.id, completed, deviceMode: mode.deviceMode })

  if (isPdf) {
    return (
      <div className={s.root} style={rootStyles}>
        <GroupingContext.Provider value={contextValue}>
          <div className={s.pdfGroup} style={{ gap: styles.indents.gap }}>
            {shuffledAnswers.map((item) => (
              <PortalGroupingItem item={item} key={item.value} />
            ))}
          </div>
          <div className={s.pdfGroup} style={{ gap: styles.indents.gap }}>
            {destination.map((e) => (
              <DestinationCard group={e} key={e.value} />
            ))}
          </div>
        </GroupingContext.Provider>
      </div>
    )
  }

  return (
    <div className={s.root} style={rootStyles}>
      <GroupingContext.Provider value={contextValue}>
        {isMobile ? <MobileElement /> : <DefaultElement />}
      </GroupingContext.Provider>
    </div>
  )
}
export default GroupingElement
